/**
 * Progrès des joueurs pendant un défi privé (migration 0704) — règles pures.
 *
 * - bulles « avatar ±HORA » : à chaque relecture, un joueur dont le nombre de
 *   lignes de brouillon validées a bougé fait apparaître une bulle avec l'écart
 *   de ses HORA de brouillon (négatif quand il efface une ligne) ;
 * - panneau facultatif : chaque joueur et son total d'HORA, en direct.
 */

import type { PrivateLiveProgressRow } from '@/lib/private-live';

export interface ProgressBubble {
  id: string;
  key: string;
  name: string;
  avatarUrl: string | null;
  delta: number;
}

/** Total d'HORA d'un joueur (réponses + brouillon). */
export function progressTotal(
  r: Pick<PrivateLiveProgressRow, 'answer_points' | 'scratch_hora'>,
): number {
  return r.answer_points + r.scratch_hora;
}

/**
 * Bulles à faire apparaître entre deux relectures. Rien à la première relecture
 * (pas de rafale à l'arrivée), jamais pour soi (sa feuille le montre déjà).
 */
export function progressBubbles(
  prev: readonly PrivateLiveProgressRow[] | null,
  next: readonly PrivateLiveProgressRow[],
  stamp: number,
): ProgressBubble[] {
  if (!prev) return [];
  const before = new Map(prev.map((r) => [r.key, r]));
  const out: ProgressBubble[] = [];
  for (const r of next) {
    if (r.is_me) continue;
    const p = before.get(r.key);
    const lines = r.lines - (p?.lines ?? 0);
    const delta = r.scratch_hora - (p?.scratch_hora ?? 0);
    if (lines === 0 && delta === 0) continue;
    out.push({
      id: `${r.key}:${stamp}`,
      key: r.key,
      name: r.name,
      avatarUrl: r.avatar_url,
      delta,
    });
  }
  return out;
}

/** « +3 », « −2 », « 0 ». */
export function formatDelta(n: number): string {
  if (n > 0) return `+${n}`;
  if (n < 0) return `−${Math.abs(n)}`;
  return '0';
}

/** Classement du panneau : total décroissant, puis nom. */
export function progressStandings(
  rows: readonly PrivateLiveProgressRow[],
): (PrivateLiveProgressRow & { total: number })[] {
  return rows
    .map((r) => ({ ...r, total: progressTotal(r) }))
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
}

const PREF_KEY = 'jr_live_progress';

/** Préférence de l'appareil : afficher le panneau des progrès. */
export function readProgressPref(): boolean {
  try {
    return localStorage.getItem(PREF_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeProgressPref(on: boolean): void {
  try {
    if (on) localStorage.setItem(PREF_KEY, '1');
    else localStorage.removeItem(PREF_KEY);
  } catch {
    /* stockage indisponible : la préférence ne survit pas, sans gravité */
  }
}
